import React from 'react';

interface SkeletonProps {
    className?: string;
    /** Render as a circle (for avatars) */
    circle?: boolean;
}

export function Skeleton({ className = '', circle = false }: SkeletonProps) {
    return (
        <div
            className={`
        animate-pulse bg-zinc-800/60 rounded-lg
        ${circle ? '!rounded-full' : ''}
        ${className}
      `}
        />
    );
}

/** Pre-composed skeleton for a card with title + 2 lines */
export function SkeletonCard({ className = '' }: { className?: string }) {
    return (
        <div className={`bg-bg-surface border border-border-subtle rounded-xl p-5 space-y-3 ${className}`}>
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
        </div>
    );
}

/** Pre-composed skeleton for a table row */
export function SkeletonRow({ cols = 4, className = '' }: { cols?: number; className?: string }) {
    return (
        <div className={`flex items-center gap-4 py-3 px-4 ${className}`}>
            {Array.from({ length: cols }).map((_, i) => (
                <Skeleton key={i} className={`h-3 ${i === 0 ? 'w-1/4' : 'flex-1'}`} />
            ))}
        </div>
    );
}

/** Pre-composed skeleton for a full table (header + n rows) */
export function SkeletonTable({
    rows = 5,
    cols = 4,
    className = '',
}: {
    rows?: number;
    cols?: number;
    className?: string;
}) {
    return (
        <div className={`border border-border-subtle rounded-xl overflow-hidden ${className}`}>
            {/* Header */}
            <div className="flex items-center gap-4 px-4 py-3 border-b border-white/5">
                {Array.from({ length: cols }).map((_, i) => (
                    <Skeleton key={i} className="h-3 flex-1" />
                ))}
            </div>
            {/* Rows */}
            {Array.from({ length: rows }).map((_, i) => (
                <SkeletonRow key={i} cols={cols} className="border-b border-white/5 last:border-b-0" />
            ))}
        </div>
    );
}

/** Pre-composed skeleton for a list of items */
export function SkeletonList({
    items = 4,
    className = '',
}: {
    items?: number;
    className?: string;
}) {
    return (
        <div className={`space-y-3 ${className}`}>
            {Array.from({ length: items }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3">
                    <Skeleton circle className="w-8 h-8 shrink-0" />
                    <div className="flex-1 space-y-2">
                        <Skeleton className="h-3 w-2/5" />
                        <Skeleton className="h-3 w-4/5" />
                    </div>
                </div>
            ))}
        </div>
    );
}

/** Pre-composed skeleton for a KPI card (large number + label) */
export function SkeletonKPI({ className = '' }: { className?: string }) {
    return (
        <div className={`bg-bg-surface border border-border-subtle rounded-xl p-5 space-y-3 ${className}`}>
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-8 w-1/2" />
            <Skeleton className="h-3 w-2/5" />
        </div>
    );
}
